import { html, LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FormValidator } from '../types/form-validator';
import { BehaviorSubject, ReplaySubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GzInputErrorWebComponent } from '../../inputError';
import { WEB_COMPONENT_GZ_INPUT_ERROR } from '../../../../webcomponents.constants';
import { FormFieldInput } from './FormFieldInput';
import { CalendarEvents } from '../../../gzDateRangePicker/types';
import { IFormGroupChild } from '../../formGroup/types/form-group-children';
import { ToggleEvents } from '../../../gzToggle/types';
import { FormEvent } from '../types/form-event.enum';
import _ from 'lodash';
import { GzTooltipWebComponent } from '../../../gzTooltip';
import { ListEvents } from '../../../gzSingleSelectList/types';

export class GzFormFieldWebComponent<TInput extends FormFieldInput<T> = FormFieldInput<any>, T = unknown>
  extends LitElement
  implements IFormGroupChild<T>
{
  @property({ type: String, reflect: true, attribute: true })
  public label: string | null = null;

  @property({ type: Boolean, reflect: true, attribute: true })
  public required = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  public readonly = false;

  /**
   * Setting this property to TRUE will display the label on top of the input. By default, the label and input are
   * horizontally aligned.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  public stacked = false;

  /**
   * Setting this property to TRUE will disable the default bottom margin for this component
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  public disableVerticalMargin = false;

  /**
   * @type {boolean} manualValidation - The validation will be done manually using the validate function.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  public manualValidation: boolean;

  public pristine = true;
  public touched = false;

  protected validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public valid$ = this.validSubject.asObservable();

  protected valueSubject: ReplaySubject<T> = new ReplaySubject<T>(1);
  public value$ = this.valueSubject.pipe(distinctUntilChanged());

  public initialValue: T | undefined;

  public stopEmitEvents = false;

  private validateInitiatorSubject: Subject<T> = new Subject<T>();
  private initiatorSubscription: Subscription | null = null;

  private validators: FormValidator<T>[] = [];

  private cachedInput: TInput | undefined = undefined;
  private cachedInputError: GzInputErrorWebComponent | null | undefined = undefined;
  private cachedToolTipText: GzTooltipWebComponent | null | undefined = undefined;

  private fieldCachedValue: T | undefined;
  private hadSlotChanged = false;

  private isUserAction = true;

  public get disabled(): boolean {
    return this.Input.disabled;
  }

  public set disabled(isDisabled: boolean) {
    if (!this.hadSlotChanged) {
      // GzFormField should _NOT_ reflect the 'disabled' in the DOM. The underlying input does.
      // But GzFormField's `disabled` can be set in the template for initialization.
      // This can cause a race-condition on init (eg: if this `disabled` setter is called on init).
      // In this scenario, make sure to update the `disabled` attribute in the dom
      // So that the code in the constructor doesn't overwrite this call
      if (isDisabled) {
        this.setAttribute('disabled', '');
      } else {
        this.removeAttribute('disabled');
      }
    }

    this.Input.disabled = isDisabled;
  }

  // this prevents overwriting the entire html
  public set innerText(text: string) {
    this.label = text;
  }

  public get Validators(): FormValidator<T>[] {
    return this.validators;
  }

  public set Validators(validators: FormValidator<T>[]) {
    this.validators = validators;
    this.triggerAutoValidation(true);
  }

  attributeChangedCallback(name: string, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    this.detectManualValidationChange(name);
  }

  updated(properties: PropertyValues): void {
    if (properties.has('readonly')) {
      this.detectReadonlyStatusChange();
    }
  }

  public get Input(): TInput {
    if (this.cachedInput === undefined) {
      this.cachedInput = this.querySelector('[gz-form-input]') as TInput;

      if (!this.cachedInput) {
        throw new Error('No FormFieldInput in FormField');
      }
    }

    return this.cachedInput;
  }

  private get InputError(): GzInputErrorWebComponent | null {
    if (this.cachedInputError === undefined) {
      this.cachedInputError = this.querySelector(WEB_COMPONENT_GZ_INPUT_ERROR) as GzInputErrorWebComponent | null;
    }

    return this.cachedInputError;
  }

  public get valid() {
    return this.validSubject.value;
  }

  public get value() {
    return this.Input.getFormValue();
  }

  public set value(value: T) {
    this.fieldCachedValue = value;

    // if the form-field isn't initialized, don't update the disabled flag on the _Input_ now
    // after the slotchange event we can send the state _to_ the Input (disabled / readonly / value)
    if (!this.hadSlotChanged) {
      return;
    }

    this.isUserAction = false;
    this.Input.setFormValue(value);

    if (this.initialValue === undefined) {
      this.initialValue = this.value;
    }

    this.isUserAction = true;
  }

  public get hasUnsavedChanges(): boolean {
    if (this.initialValue === undefined) {
      if (typeof this.value === 'number') return true;

      return !_.isEmpty(this.value);
    }

    // use input method for comparing values
    return !this.Input.areFormValuesEqual(this.initialValue, this.value);
  }

  public refreshInitialFormValue(): void {
    this.initialValue = this.value;
  }

  public validate(markAsTouched = false): void {
    if (this.Input.disabled || this.readonly) {
      return;
    }

    if (markAsTouched) {
      this.touched = true;
    }

    this.triggerValidation(true);
  }

  public async dryValidate(): Promise<boolean> {
    return this.performValidationCycle(true);
  }

  public setError(error: string): void {
    // not touched directly by the user in the UI, but should be considered as "touched"
    // (usually by a user click on another component)
    this.touched = true;
    this.markAsInvalid(error);
  }

  public resetField() {
    this.touched = false;
    this.pristine = true;

    if (this.manualValidation) {
      this.validSubject.next(false);
    } else {
      this.markAsValid();
    }
  }

  /**
   * If manualValidation is enabled, the form field will be set as invalid.
   *
   * @param {string} name
   * @private
   */
  private detectManualValidationChange(name: string) {
    if (name !== 'manualvalidation') {
      return;
    }

    if (this.manualValidation) {
      this.touched = false;
      this.markAsInvalid('invalid');
    } else {
      this.triggerAutoValidation(true);
    }
  }

  protected triggerAutoValidation(force = false) {
    if (this.manualValidation || this.Input.disabled || this.readonly) {
      return;
    }

    this.triggerValidation(force);
  }

  protected triggerValidation(force = false) {
    if (force) {
      this.performValidationCycle().catch(console.error);
      return;
    }

    this.validateInitiatorSubject.next(this.Input.getFormValue());
  }

  protected async firstUpdated(changedProperties): Promise<void> {
    super.firstUpdated(changedProperties);

    // if field is already invalid, no need revalidate (right after first update)
    if (!this.valid) {
      this.triggerAutoValidation();
    }

    this.Input.addEventListener('focus', () => {
      this.pristine = false;
    });

    this.Input.addEventListener('blur', () => {
      const prevTouchedValue = this.touched;
      this.touched = true;

      if (!prevTouchedValue) {
        this.triggerAutoValidation(true);
      }
    });

    // value change
    this.Input.addEventListener('change', this.onValueChange.bind(this));
    this.Input.addEventListener('selectionChanged', this.onValueChange.bind(this));
    this.Input.addEventListener(ToggleEvents.TOGGLE_CHANGE, this.onValueChange.bind(this));
    this.Input.addEventListener(CalendarEvents.DATE_IS_SELECTED, this.onValueChange.bind(this));
    this.Input.addEventListener('radioSelectionChanged', this.onValueChange.bind(this));
    this.Input.addEventListener(ListEvents.SELECTION_CHANGE, this.onValueChange.bind(this));

    // on disable change
    this.Input.addEventListener(FormEvent.OnDisableInput, this.detectDisabledStatusChange.bind(this));
  }

  public connectedCallback(): void {
    super.connectedCallback();

    this.shadowRoot?.addEventListener('slotchange', () => {
      if (this.hadSlotChanged) {
        return;
      }

      this.hadSlotChanged = true;

      // attribute only used for init (if set)
      const isInputDisabled = this.hasAttribute('disabled');
      if (isInputDisabled) {
        this.removeAttribute('disabled');
      }

      // previously the field wasn't initialized; setting the state in the Input here:
      this.Input.setEnabled(!isInputDisabled);
      this.Input.setReadonly(this.readonly);

      // it's possible that the value has not been set using `this.value`, so don't override it
      if (this.fieldCachedValue !== undefined) {
        this.isUserAction = false;
        this.Input.setFormValue(this.fieldCachedValue);
        this.isUserAction = true;
      }
    });

    if (null === this.initiatorSubscription || this.initiatorSubscription.closed) {
      // make sure to wait a bit between validations
      this.initiatorSubscription = this.validateInitiatorSubject
        .asObservable()
        .pipe(debounceTime(250))
        .subscribe(() => {
          this.performValidationCycle().catch(console.error);
        });
    }
  }

  disconnectedCallback() {
    this.initiatorSubscription?.unsubscribe();
  }

  private onValueChange(): void {
    this.fieldCachedValue = this.Input.getFormValue();
    this.valueSubject.next(this.fieldCachedValue);
    if (!this.pristine) {
      this.touched = true;
    }

    if (this.manualValidation) {
      this.markAsValid();
      this.validSubject.next(false);
    } else {
      this.triggerAutoValidation(false);
    }

    this.emitValueChangedEvent();
  }

  private emitValueChangedEvent(): void {
    if (this.stopEmitEvents) {
      return;
    }

    const changeEvent = new CustomEvent<{ value: T; isUserAction: boolean }>(FormEvent.ValueChange, {
      detail: {
        value: this.Input.getFormValue(),
        isUserAction: this.isUserAction,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(changeEvent);
  }

  private async performValidationCycle(dryRun = false): Promise<boolean> {
    for (const validator of this.validators) {
      const result = await validator(this.value);

      if (!result.valid) {
        if (!dryRun) {
          this.markAsInvalid(result.error);
        }

        return false;
      }
    }

    if (!dryRun) {
      this.markAsValid();
    }

    return true;
  }

  protected markAsInvalid(error: string) {
    this.validSubject.next(false);
    // if the field is not touched -> don't show the error
    if (!this.touched) {
      return;
    }

    this.Input.markAsInvalid();
    if (this.InputError) {
      this.InputError.Error = error;
    } else {
      this.Input.errorTooltipText = error;
    }
    this.emitState(FormEvent.FormInvalid, error);
  }

  private emitState(eventName, error?: string): void {
    if (this.stopEmitEvents) {
      return;
    }

    const changeEvent = new CustomEvent<{ error: string | undefined }>(eventName, {
      detail: {
        error: error,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(changeEvent);
  }

  protected markAsValid() {
    this.validSubject.next(true);

    this.Input.markAsValid();

    if (this.InputError) {
      this.InputError.Error = null;
    }

    this.emitState(FormEvent.FormValid);
  }

  public static get styles() {
    return styles;
  }

  private renderLabel() {
    return html`<span class="form-field-label ${this.required ? 'required' : ''}" part="form-field-label"
      >${this.label}</span
    >`;
  }

  public render() {
    return html`
      <div class="wrapper">
        ${this.label ? this.renderLabel() : ''}
        <div class="input-wrapper">
          <slot></slot>
        </div>
      </div>
    `;
  }

  private detectDisabledStatusChange() {
    if (this.Input.disabled) {
      this.resetField();
      return;
    }

    if (this.manualValidation) {
      this.validSubject.next(false);
    } else {
      this.triggerAutoValidation(true);
    }
  }

  private detectReadonlyStatusChange() {
    if (this.hadSlotChanged) {
      this.Input.setReadonly(this.readonly);
    }

    if (this.readonly) {
      this.resetField();
      return;
    }

    if (this.manualValidation) {
      this.validSubject.next(false);
    } else {
      this.triggerAutoValidation(true);
    }
  }

  public discardChanges(): void {
    if (this.initialValue !== undefined) {
      this.value = this.initialValue;
    }
  }
}
